<template>
  <div v-if="visible" class="overlay-modal" @click.self="$emit('cerrar')">
    <div class="modal-visor">
      <div class="visor-header">
        <div>
          <h4 class="visor-titulo">{{ titulo }}</h4>
          <p class="visor-sub">{{ subtitulo }}</p>
        </div>
        <button type="button" class="btn-cerrar" title="Cerrar" @click="$emit('cerrar')">✕</button>
      </div>

      <div class="visor-cuerpo">
        <!-- La misma tabla se muestra al calificar: vive en su propio
             componente para que las dos vistas lean idéntica la rúbrica. -->
        <RubricaTabla :rubrica="rubrica" />
      </div>

      <div class="visor-pie">
        <button type="button" class="btn salir" @click="$emit('cerrar')">Cerrar</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Visor de una rúbrica en modo consulta.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { computed } from 'vue'
import type { CriterioEvaluacion } from '@/interfaces/CriterioEvaluacion'
import RubricaTabla from '@/components/RubricaTabla.vue'

const props = defineProps<{
  visible: boolean
  criterio: CriterioEvaluacion | null
  nombreTipoComponente?: string
}>()

defineEmits<{ (e: 'cerrar'): void }>()

const rubrica = computed(() => props.criterio?.criterio ?? null)

const titulo = computed(() => {
  switch (props.criterio?.tipo_informacion) {
    case 'documental': return 'Criterios de Evaluación de la Información Documental'
    case 'cuantitativa': return 'Criterios de Evaluación de la Información Cuantitativa'
    case 'opinion': return 'Criterios de Evaluación de la Información de Opinión'
    default: return 'Rúbrica'
  }
})

const subtitulo = computed(() => {
  switch (props.criterio?.tipo_informacion) {
    case 'documental': return 'Ficha de revisión documental'
    case 'cuantitativa': return 'Ficha de revisión de indicadores estadísticos'
    case 'opinion': return 'Tabla de valoración'
    default: return props.nombreTipoComponente ?? ''
  }
})

</script>

<style scoped>
.overlay-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1.5rem;
}

.modal-visor {
  background: #fff;
  border-radius: 10px;
  width: 100%;
  max-width: 1200px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  font-family: var(--font-family, inherit);
}

.visor-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.1rem 1.4rem 0.9rem;
  border-bottom: 1px solid #ececec;
}

.visor-titulo {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f4e79;
}

.visor-sub {
  margin: 0.2rem 0 0;
  font-size: 0.85rem;
  color: #666;
}

.btn-cerrar {
  border: none;
  background: transparent;
  font-size: 1.1rem;
  cursor: pointer;
  color: #666;
  line-height: 1;
  padding: 0.2rem 0.4rem;
}

.btn-cerrar:hover {
  color: #b00020;
}

.visor-cuerpo {
  padding: 1.2rem 1.4rem;
  overflow-y: auto;
}

/* La tabla puede ser ancha: scrolla dentro de su caja, nunca la página. */
.tabla-scroll {
  overflow-x: auto;
}

.tabla-rubrica {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

.tabla-rubrica th {
  background: #5b9bd5;
  color: #fff;
  font-weight: 700;
  text-align: center;
  padding: 0.55rem 0.6rem;
  border: 1px solid #b8cce4;
  white-space: nowrap;
}

.tabla-rubrica td {
  border: 1px solid #b8cce4;
  padding: 0.5rem 0.6rem;
  vertical-align: middle;
  color: #222;
}

/* Filas alternas, como en la hoja de cálculo original. */
.tabla-rubrica tbody tr:nth-child(odd) {
  background: #deeaf6;
}

.tabla-rubrica tbody tr:nth-child(even) {
  background: #fff;
}

.celda-grado {
  font-weight: 700;
  white-space: nowrap;
}

.celda-centro {
  text-align: center;
  font-weight: 600;
}

.celda-escala {
  text-align: center;
  white-space: nowrap;
}

.celda-juicio {
  min-width: 320px;
  text-align: justify;
}

.col-grado {
  min-width: 130px;
}

.col-escala {
  min-width: 110px;
}

.bloque {
  margin-top: 1.4rem;
}

.bloque-titulo {
  margin: 0 0 0.6rem;
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f4e79;
}

.conversion {
  margin: 0.2rem 0;
  font-size: 0.83rem;
  color: #333;
}

.vacio {
  margin: 0;
  color: #888;
  font-size: 0.9rem;
}

.visor-pie {
  display: flex;
  justify-content: flex-end;
  padding: 0.9rem 1.4rem;
  border-top: 1px solid #ececec;
}

.btn {
  padding: 8px 20px;
  height: 38px;
  font-weight: 600;
  font-size: 0.83rem;
  font-family: inherit;
  border-radius: 6px;
  cursor: pointer;
}

.btn.salir {
  background: #fff;
  border: 1px solid #ccc;
}

.btn.salir:hover {
  background: #f2f2f2;
}
</style>
